import type { UnifiedCampaign, CampaignStatus } from '@/types/unifiedCampaign';

export interface ValidationResult {
  isValid: boolean;
  error?: string;
  conflictingCampaign?: UnifiedCampaign;
}

/**
 * Validates if an inbound campaign can be activated on a specific phone number
 * Rule: Only one active inbound campaign per phone number allowed
 */
export function validateInboundCampaignActivation(
  campaignToActivate: UnifiedCampaign,
  allCampaigns: UnifiedCampaign[]
): ValidationResult {
  // Only validate inbound campaigns
  if (campaignToActivate.campaign_type !== 'inbound') {
    return { isValid: true };
  }

  // Must have a phone number to validate
  if (!campaignToActivate.phone_number) {
    return { 
      isValid: false, 
      error: 'Phone number is required for inbound campaigns' 
    };
  }

  // Find any active inbound campaign using the same phone number
  const conflictingCampaign = allCampaigns.find(campaign => 
    campaign.id !== campaignToActivate.id &&
    campaign.campaign_type === 'inbound' &&
    campaign.phone_number === campaignToActivate.phone_number &&
    campaign.status === 'active'
  );

  if (conflictingCampaign) {
    return {
      isValid: false,
      error: `Cannot activate inbound campaign. Phone number ${campaignToActivate.phone_number} is already in use by active campaign "${conflictingCampaign.name}". Please pause the existing campaign first.`,
      conflictingCampaign
    };
  }

  return { isValid: true };
}

/**
 * Validates if a phone number can be assigned to a campaign
 * Checks for cross-VAPI key conflicts and inbound exclusivity
 */
export function validatePhoneNumberAssignment(
  campaign: UnifiedCampaign,
  phoneNumber: string,
  vapiKeyName: string,
  allCampaigns: UnifiedCampaign[]
): ValidationResult {
  // Check for cross-VAPI key conflicts
  const crossVapiConflict = allCampaigns.find(existingCampaign =>
    existingCampaign.id !== campaign.id &&
    existingCampaign.phone_number === phoneNumber &&
    existingCampaign.status !== 'cancelled' && // Cancelled campaigns don't reserve numbers
    getVapiKeyFromCampaign(existingCampaign) !== vapiKeyName
  );

  if (crossVapiConflict) {
    const conflictingVapiKey = getVapiKeyFromCampaign(crossVapiConflict);
    return {
      isValid: false,
      error: `Phone number ${phoneNumber} is already assigned to campaign "${crossVapiConflict.name}" using VAPI key "${conflictingVapiKey}". Cannot use with different VAPI key "${vapiKeyName}".`,
      conflictingCampaign: crossVapiConflict
    };
  }

  // Check inbound campaign exclusivity (only for active campaigns)
  if (campaign.campaign_type === 'inbound' && campaign.status === 'active') {
    return validateInboundCampaignActivation(campaign, allCampaigns);
  }

  return { isValid: true };
}

/**
 * Gets the VAPI key name from a campaign's ai_settings
 * Defaults to 'api_key' for backward compatibility
 */
export function getVapiKeyFromCampaign(campaign: UnifiedCampaign): string {
  if (campaign.ai_settings && typeof campaign.ai_settings === 'object' && 'vapi_key_name' in campaign.ai_settings) {
    return (campaign.ai_settings as any).vapi_key_name || 'api_key';
  }
  return 'api_key';
}

/**
 * Gets phone number reservations by VAPI key
 * Returns a map of phone numbers to campaigns that are actively using them
 */
export function getPhoneNumberReservations(campaigns: UnifiedCampaign[]): Map<string, UnifiedCampaign[]> {
  const reservations = new Map<string, UnifiedCampaign[]>();

  campaigns.forEach(campaign => {
    // Only active, paused, and draft campaigns reserve phone numbers
    // Cancelled and completed campaigns release their reservations
    if (!campaign.phone_number || 
        campaign.status === 'cancelled' || 
        campaign.status === 'completed') {
      return;
    }

    const key = `${campaign.phone_number}:${getVapiKeyFromCampaign(campaign)}`;
    
    if (!reservations.has(key)) {
      reservations.set(key, []);
    }
    
    reservations.get(key)!.push(campaign);
  });

  return reservations;
}